import React, { Component } from 'react';
import axios from 'axios';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from 'react-router-dom';
import { sortBy, clone } from 'lodash';
import moment from 'moment';
import { CSVLink } from "react-csv";

// material
import { Container, Table, TableHead, TableRow, TableCell, TableBody, Hidden } from '@material-ui/core';
import { withStyles, Theme } from '@material-ui/core/styles';
// app
import { updateSession, addAuth, openSnackbar, closeSnackbar } from '../../store/action-creators';
// containers
import AppContainer from '../../containers/App';
// models
import { Auth, Session } from '../../entities/auth';
import { Campaign, CampaignContact, TabType, CampignContactFavorite, emptyCampaignContact } from '../../entities/site';
// components
import Loading from '../../components/site/Loading';
import BackButton from '../../components/buttons/BackButton';
// services
import AuthService from '../../services/auth';

/*

{
    "query": {
        "bool": {
            "must": [
                {
                    "match": {
                        "campaignId": "f2e001b0-64bf-11eb-b00c-3957664403ad"
                    }
                },
                {
                    "match": {
                        "campaignId": "f2e001b0-64bf-11eb-b00c-3957664403ad"
                    }
                },
                {
                    "match": {
                        "status": "clickThrough"
                    }
                }
            ]
        }
    },
    "size": 100
}


{
"aggs": 
  {
    "total_favorites": {
      "terms": {
        "field": "favoriteId" 

    },
  "size": 100
}


*/



const styles: any = (theme: Theme) => ({
  title: {
    fontSize: "24px",
    fontFamily: "Roboto Medium",
    [theme.breakpoints.down('xs')]: {
      fontSize: "20px"
    }
  },
  campaignContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
    padding: "20px 5px"
  },
  pageContainer: {
    padding: "0px 20px",
    marginBottom: "100px",
    [theme.breakpoints.down('sm')]: {
      padding: "0px !important",
    }
  }
})



interface CampaignFavoritesProps {
  history: any;
  auth: Auth;
  session: Session;
  updateSession(session: Session): void;
  openSnackbar(message: string): void;
  closeSnackbar(): void;
}

interface CampaignFavoritesState {
  loading: boolean;
  campaigns: Campaign[];
  campaignContactFavorites: CampaignContact[];
}

class CampaignFavoritesPage extends Component<CampaignFavoritesProps, CampaignFavoritesState> {

  constructor(props: CampaignFavoritesProps) {
    super(props);
    this.state = {
      loading: false,
      campaigns: [],
      campaignContactFavorites: []
    }
  }

  componentDidMount = async () => {

    this.fetchCampaigns();
    this.fetchCampaignFavorites();
  }

  onNavigate = (route: string) => {
    this.props.history.push(route);
  }

  fetchCampaigns = async () => {

    try {

      const campaignsRes = await axios.get(`${AuthService.getApiUrls().site}/campaigns`);

      this.setState({
        campaigns: campaignsRes.data.results,
        loading: false
      });

    } catch (error) {
      console.log('error', error);
    }

  }

  fetchCampaignFavorites = async () => {

    try {

      this.setState({ loading: true });

      const user = AuthService.getUser();
      const favoritesRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/contacts/favorites/user/${user?.userId}`);

      let contacts: CampaignContact[] = [];
      favoritesRes.data.results.forEach((favorite: CampignContactFavorite) => {
        const newCampaignContact = clone(emptyCampaignContact);
        newCampaignContact.id = favorite.contactId;
        newCampaignContact.campaignId = favorite.campaignId;
        newCampaignContact.email = favorite.contactEmail;
        newCampaignContact.phoneNumber = favorite.contactPhone;
        newCampaignContact.firstName = favorite.contactFirstName;
        newCampaignContact.lastName = favorite.contactLastName;
        newCampaignContact.favoriteId = `${user?.userId}|${favorite.contactId}|${favorite.id}`;
        newCampaignContact.createdDate = favorite.updatedDate;
        newCampaignContact.newMessage = favorite.newMessage || false;
        newCampaignContact.campaignName = favorite.campaignName;
        contacts.push(newCampaignContact);
      });
      contacts = sortBy(contacts, 'firstName');

      const newSession = this.props.session;
      newSession.campaignContacts = contacts;
      this.props.updateSession(newSession);

      this.setState({
        campaignContactFavorites: contacts,
        loading: false
      });

    } catch (error) {
      console.log('error', error);
      this.setState({ loading: false });
    }

  }

  handleShowLoading = () => {
    this.setState({ loading: true });
  }

  handleContactClick = (campaignContactFavorite: CampaignContact) => {
    this.props.history.push(`/campaign/${campaignContactFavorite.campaignId}/contact/${campaignContactFavorite.id}/type/favorites`);
  }

  handleCampaignClick = (campaignContactFavorite: CampaignContact) => {
    this.props.history.push(`/campaign/${campaignContactFavorite.campaignId}`);
  }

  render() {
    const { classes }: any = this.props;
    const today = new Date().toLocaleDateString();

    return (
      <AppContainer showOption={false} private={false} maxWidth="lg">
        <Loading open={this.state.loading} />
        <BackButton text="Back to campaigns" onClick={() => this.props.history.push(`/campaigns`)} />
        <div className={classes.pageContainer}>
          <div className={classes.campaignContainer}>
            <div className={classes.title}>Favorites</div>
            <CSVLink
                data={this.state.campaignContactFavorites}
                filename={`campaign-favorites-${today}.csv`}
                className="csv-link"
              >Export to cvs</CSVLink>
          </div>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <Hidden only={['xs', 'sm']}>
                  <TableCell>Email</TableCell>
                </Hidden>
                <Hidden only={['xs', 'sm']}>
                  <TableCell>Campaign</TableCell>
                </Hidden>
                <Hidden only={['xs', 'sm']}>
                  <TableCell>Created Date</TableCell>
                </Hidden>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {this.state.campaignContactFavorites.map((contact: CampaignContact, index) => (
                <TableRow key={`contact-${index}`}>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ color: '#00ABC7', fontSize: 18, cursor: 'pointer' }}
                    onClick={() => this.handleContactClick(contact)}
                  >
                    {contact.firstName} {contact.lastName}
                  </TableCell>
                  <TableCell>{contact.phoneNumber}</TableCell>
                  <Hidden only={['xs', 'sm']}>
                    <TableCell>{(contact.email ? contact.email : 'n/a')}</TableCell>
                  </Hidden>
                  <Hidden only={['xs', 'sm']}>
                    <TableCell>
                      <div 
                        style={{ color: '#00ABC7', cursor: 'pointer' }}
                        onClick={() => this.handleCampaignClick(contact)}>
                          {contact.campaignName}
                      </div>
                    </TableCell>
                  </Hidden>
                  <Hidden only={['xs', 'sm']}>
                    <TableCell>{moment(contact.createdDate).format('MMM DD, YYYY')}</TableCell>
                  </Hidden>
                  <TableCell />
                </TableRow>
              ))}
            </TableBody>
          </Table>

        </div>
      </AppContainer>
    )
  }
}

const mapStateToProps = (state: any) => {
  return {
    auth: state.auth.auth,
    session: state.session.session
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators({ updateSession, addAuth, openSnackbar, closeSnackbar }, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter<any, any>(withStyles(styles)(CampaignFavoritesPage)));



// WEBPACK FOOTER //
// ./src/pages/campaign/CampaignFavorites.tsx